import type { Selection } from './useTableSelection';
import { SelectionType } from './useTableSelection';

export interface CellBorders {
  inRange: boolean;
  leftBorder: boolean;
  rightBorder: boolean;
  topBorder: boolean;
  bottomBorder: boolean;
}

const getCellBordersForSelection = (
  selection: Selection | null,
  {
    rowIndex,
    columnIndex,
    rowCount,
    columnCount,
  }: {
    rowIndex: number;
    columnIndex: number;
    rowCount: number;
    columnCount: number;
  },
): CellBorders => {
  const isLastRow = rowIndex === rowCount - 1;
  const isLastColumn = columnIndex === columnCount - 1;
  switch (selection?.type) {
    case SelectionType.Row: {
      const { rows } = selection;
      // eslint-disable-next-line @typescript-eslint/naming-convention -- Add boolean prefix next time file is edited
      const inRange = rows.includes(rowIndex);
      return {
        inRange,
        leftBorder: inRange && columnIndex === 0,
        rightBorder: inRange && isLastColumn,
        topBorder: inRange && (rowIndex === 0 || !rows.includes(rowIndex - 1)),
        bottomBorder: inRange && (isLastRow || !rows.includes(rowIndex + 1)),
      };
    }

    case SelectionType.Column: {
      const { columns } = selection;
      // eslint-disable-next-line @typescript-eslint/naming-convention -- Add boolean prefix next time file is edited
      const inRange = columns.includes(columnIndex);
      return {
        inRange,
        topBorder: inRange && rowIndex === 0,
        bottomBorder: inRange && isLastRow,
        leftBorder:
          inRange && (columnIndex === 0 || !columns.includes(columnIndex - 1)),
        rightBorder:
          inRange && (isLastColumn || !columns.includes(columnIndex + 1)),
      };
    }

    case SelectionType.Table: {
      return {
        inRange: true,
        leftBorder: columnIndex === 0,
        rightBorder: isLastColumn,
        topBorder: rowIndex === 0,
        bottomBorder: isLastRow,
      };
    }

    default:
      return {
        inRange: false,
        leftBorder: false,
        rightBorder: false,
        topBorder: false,
        bottomBorder: false,
      };
  }
};

export default getCellBordersForSelection;
